<template>
  <div class="reset-password-component">
    <!-- Loading state -->
    <div v-if="loading" class="loading-state">
      <p>Loading password requirements...</p>
    </div>

    <!-- Success state -->
    <div v-else-if="success" class="success-message">
      <h3>Password Updated Successfully!</h3>
      <p v-if="isExpired">
        Your expired password has been reset successfully. You can now login
        with your new password.
      </p>
      <p v-else>Your password has been reset successfully.</p>
      <button @click="redirectToLogin" class="btn-secondary">
        <span v-if="!isExpired" class="osci osci-chevron-left"></span>
        <span v-if="isExpired">Continue to dashboard</span>
        <span v-else>Back to login</span>
        <span v-if="isExpired" class="osci osci-chevron-right"></span>
      </button>
    </div>

    <!-- Form state -->
    <div v-else class="password-form">
      <!-- Header Section (moved from .cshtml) -->
      <div class="password-form-header">
        <h3 v-if="isExpired">Your password has expired</h3>
        <h3 v-else>Reset your password</h3>
        
        <p v-if="isExpired">
          <span class="osci osci-notification-circle" style="color: #f39c12;"></span> 
          Your password has expired and must be reset to continue.
        </p>
        <p v-else>
          <span class="osci osci-notification-circle"></span> 
          Please enter your new password below.
        </p>
      </div>
      <!-- Password Requirements at the top -->
      <password-strength :password="password" :policy="passwordPolicy" />


      <div class="form-group">
        <!-- <label for="password">New Password</label> -->
        <password-input
          id="password"
          v-model="password"
          placeholder="Enter your new password"
          :disabled="submitting"
        />
      </div>
      <div class="form-group">
        <!-- <label for="confirmPassword">Confirm Password</label> -->
        <password-input
          id="confirmPassword"
          v-model="confirmPassword"
          placeholder="Confirm your new password"
          :disabled="submitting"
        />
      </div>

      <!-- Password Strength at the bottom -->
      <password-requirements
        :password="password"
        :confirm-password="confirmPassword"
        :policy="passwordPolicy"
        :errors="errors"
        :strength-level="(passwordPolicy && passwordPolicy.allowWeakPasswords) ? 4 : strengthLevel"
        :requirements="getPasswordRequirements()"
      />
      <submit-button
        :disabled="!isFormValid || submitting"
        :loading="submitting"
        @click="submitPasswordReset"
        text="Reset Password"
      />
      
      <!-- Back to Login Link -->
      <div class="form-actions">
        <a href="/Account/Login" class="btn-secondary">
          <span class="osci osci-chevron-left"></span> Back to login
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import PasswordInput from "./components/passwordinput.vue";
import PasswordRequirements from "./components/passwordrequirements.vue";
import PasswordStrength from "./components/passwordstrength.vue";
import SubmitButton from "./components/submitbutton.vue";

export default {
  name: "ResetPassword",
  components: {
    PasswordInput,
    PasswordRequirements,
    PasswordStrength,
    SubmitButton,
  },
  props: {
    userId: {
      type: String,
      required: true,
    },
    code: {
      type: String,
      required: true,
    },
    isExpired: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      password: "",
      confirmPassword: "",
      passwordPolicy: null,
      loading: true,
      submitting: false,
      success: false,
      errors: [],
      validationTimeout: null, // Add for debouncing
      backendStrength: "VeryWeak", // Add for backend strength
      allowWeakPasswords: false, // Add for policy flag
    };
  },
  computed: {
    strengthLevel() {
      if (!this.password) return 0;

      let score = 0;

      // Length scoring
      if (this.password.length >= (this.passwordPolicy && this.passwordPolicy.minimumLength || 10))
        score += 20;
      if (this.password.length >= 20) score += 10;

      // Character variety
      if (/[A-Z]/.test(this.password)) score += 15;
      if (/[a-z]/.test(this.password)) score += 15;
      if (/\d/.test(this.password)) score += 15;
      if (/[!@#$%^&*()_+=\[\]{};:<>|./?,-]/.test(this.password)) score += 25;

      // Complexity bonus
      const uniqueChars = new Set(this.password).size;
      score += Math.min(uniqueChars * 1, 10);

      // Pattern penalties
      if (/(.)\1{2,}/.test(this.password)) score -= 10; // Repeated characters
      if (/123|abc|qwe/i.test(this.password)) score -= 10; // Sequential patterns

      // Determine strength level (0-4)
      if (score >= 80) return 4; // Very Strong
      if (score >= 60) return 3; // Strong
      if (score >= 40) return 2; // Fair
      if (score >= 20) return 1; // Weak
      return 0; // Very Weak
    },

    passwordsMatch() {
      return this.password === this.confirmPassword;
    },

    isFormValid() {
      if (!this.passwordPolicy || !this.password || !this.confirmPassword) {
        return false;
      }

      // Check basic requirements (all required ones for form validation)
      const requirements = this.getPasswordRequirements();
      const allRequirementsMet = requirements
        .filter(req => req.required !== false)
        .every((req) => req.met);
      const passwordsMatch = this.passwordsMatch;

      // Check for blocked passwords explicitly
      const hasBlockedPassword = requirements.some(req => 
        req.name === 'blocked' && req.warning && !req.met
      );

      // Only block if there are critical backend validation errors
      // Allow submission if basic requirements are met, passwords match, and strength is adequate
      const hasBackendBlockingErrors = this.errors.some(error => {
        const lowerError = error.toLowerCase();
        return (
          lowerError.includes('blocked') || 
          lowerError.includes('too weak') ||
          (lowerError.includes('not allowed') && !lowerError.includes('commonly used'))
        );
      });
      
      // Require at least "Strong" strength (level 3) as per requirements
      const hasAdequateStrength = this.strengthLevel >= 3;
      
      return allRequirementsMet && passwordsMatch && !hasBlockedPassword && !hasBackendBlockingErrors && hasAdequateStrength;
    },
  },
  mounted() {
    console.log("Reset Password Component mounted with:", {
      userId: this.userId,
      code: this.code,
      isExpired: this.isExpired,
    });

    if (this.userId) {
      this.fetchPasswordPolicy();
    } else {
      console.error("No userId provided to component");
      this.errors = ["Invalid user session. Please try again."];
      this.loading = false;
    }
  },

  methods: {
    async fetchPasswordPolicy() {
      try {
        this.loading = true;
        this.errors = [];

        const response = await fetch(
          `/api/passwordpolicy/public/requirements/${this.userId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error(
            `Failed to fetch password policy: ${response.status} ${response.statusText}`
          );
        }

        const policy = await response.json();
        console.log("Received password policy:", policy);

        this.passwordPolicy = {
          minimumLength: policy.minimumLength || policy.MinimumLength,
          requireUppercase: policy.requireUppercase || policy.RequireUppercase,
          requireLowercase: policy.requireLowercase || policy.RequireLowercase,
          requireDigit: policy.requireDigit || policy.RequireDigit,
          requireSpecialCharacter: policy.requireSpecialCharacter || policy.RequireSpecialCharacter,
          allowWeakPasswords: policy.allowWeakPasswords || policy.AllowWeakPasswords,
          blockedPasswords: policy.blockedPasswords || policy.BlockedPasswords || [],
        };
      } catch (error) {
        console.error("Error fetching password policy:", error);
        this.errors = [
          "Failed to load password requirements. Using default policy.",
        ];

        // Fallback to default policy
        this.passwordPolicy = this.getDefaultPolicy();
      } finally {
        this.loading = false;
      }
    },

    getPasswordRequirements() {
      if (!this.passwordPolicy) return [];

      const requirements = [
        {
          name: "length",
          met: this.password.length >= this.passwordPolicy.minimumLength,
          text: `At least ${this.passwordPolicy.minimumLength} characters`,
          required: true,
        },
      ];

      if (this.passwordPolicy.requireUppercase) {
        requirements.push({
          name: "uppercase",
          met: /[A-Z]/.test(this.password),
          text: "At least one uppercase letter (A-Z)",
          required: true,
        });
      }

      if (this.passwordPolicy.requireLowercase) {
        requirements.push({
          name: "lowercase",
          met: /[a-z]/.test(this.password),
          text: "At least one lowercase letter (a-z)",
          required: true,
        });
      }

      if (this.passwordPolicy.requireDigit) {
        requirements.push({
          name: "digit",
          met: /\d/.test(this.password),
          text: "At least one number (0-9)",
          required: true,
        });
      }

      if (this.passwordPolicy.requireSpecialCharacter) {
        requirements.push({
          name: "special",
          met: /[!@#$%^&*()_+=\[\]{};:<>|./?,-]/.test(this.password),
          text: "At least one special character (!@#$%^&*)",
          required: true,
        });
      }

      // Check for blocked passwords/keywords - always add but control visibility
      if (this.passwordPolicy.blockedPasswords && this.passwordPolicy.blockedPasswords.length > 0) {
        const foundBlockedWord = this.password ? this.passwordPolicy.blockedPasswords.find(blocked => 
          this.password.toLowerCase().includes(blocked.toLowerCase())
        ) : null;
        
        const containsBlockedWord = !!foundBlockedWord;
        const dynamicText = foundBlockedWord 
          ? `Password contains commonly blocked word '${foundBlockedWord}'`
          : "Must not contain common passwords or blocked words";
        
        requirements.push({
          name: "blocked",
          met: !containsBlockedWord,
          text: dynamicText,
          required: true, // Now enforced on frontend to block reset button
          warning: containsBlockedWord,
          foundWord: foundBlockedWord,
          showOnlyWhenRelevant: true // Special flag for this requirement
        });
      }

      return requirements;
    },

    async submitPasswordReset() {
      if (!this.isFormValid) return;

      try {
        this.submitting = true;
        this.errors = [];

        const response = await fetch("/api/account/reset-password", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            UserId: this.userId,
            Code: this.code,
            Password: this.password,
            ConfirmPassword: this.confirmPassword,
          }),
        });

        const result = await response.json();

        if (result.success) {
          this.success = true;
        } else {
          this.errors = result.errors || [
            "An error occurred. Please try again.",
          ];
        }
      } catch (error) {
        console.error("Error resetting password:", error);
        this.errors = [
          "Network error. Please check your connection and try again.",
        ];
      } finally {
        this.submitting = false;
      }
    },

    redirectToLogin() {
      if (this.isExpired) {
        window.location.href =
          "/Account/Login?message=Password updated successfully. Please log in with your new password.";
      } else {
        window.location.href = "/Account/Login";
      }
    },



    async validatePasswordAgainstPolicy() {
      if (!this.password || !this.userId) {
        this.errors = [];
        this.backendStrength = "VeryWeak";
        return;
      }

      try {
        console.log("Validating password for user:", this.userId);

        const response = await fetch("/api/passwordpolicy/public/validate", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            UserId: this.userId,
            Password: this.password,
          }),
        });

        if (response.ok) {
          const result = await response.json();
          console.log("Validation result:", result);

          this.errors = result.errors || [];
          this.backendStrength = result.strength || "VeryWeak";
          this.allowWeakPasswords = result.allowWeakPasswords || false;
        } else {
          console.error("Password validation failed:", response.status);
          // Don't show errors for validation failures during typing
        }
      } catch (error) {
        console.error("Error validating password:", error);
        // Don't show errors for validation failures during typing
      }
    },

    isValidUserId(userId) {
      // Check if userId is a valid GUID format
      const guidRegex =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      return guidRegex.test(userId);
    },

    getDefaultPolicy() {
      return {
        minimumLength: 10,
        requireUppercase: false,
        requireLowercase: false,
        requireDigit: true,
        requireSpecialCharacter: true,
        allowWeakPasswords: false,
        blockedPasswords: [],
      };
    },
  },

  watch: {
    password: {
      handler(newPassword) {
        // Debounce the API call
        clearTimeout(this.validationTimeout);
        this.validationTimeout = setTimeout(() => {
          this.validatePasswordAgainstPolicy();
        }, 500);
      },
    },
  },
};
</script>

<style scoped>
.reset-password-component {
  max-width: 500px;
  margin: 0 auto;
  padding-bottom: 20px;
}

.loading-state {
  text-align: center;
  padding: 40px 20px;
}

.success-message {
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.success-message h3 {
  color: #fff;
  font-size: 16px;
  font-weight: bold;
  line-height: 1;
}

.success-message p {
  color: #fff;
  font-size: 12px;
  line-height: 1;
  margin-bottom: 30px;
}

.success-message .btn {
  min-width: 150px;
  padding: 12px 24px;
  margin-top: 10px;
}

.password-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.password-form-header {
  text-align: center;
}

.password-form-header h3 {
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
}

.password-form-header p {
  margin: 0;
  font-size: 12px;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  /* gap: 8px; */
}

.password-form-header .osci-notification-circle {
  font-size: 16px;
}

.form-actions {
  text-align: center;
  margin-top: 16px;
}

.form-actions .btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  text-decoration: none;
  color: #666;
  font-size: 14px;
  transition: color 0.2s ease;
}

.form-actions .btn:hover {
  color: #333;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  font-weight: 600;
  color: white;
}

.btn {
  padding: 12px 24px;
  border: none;
  border-radius: 4px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.btn--primary {
  background-color: #005eb8;
  color: white;
}

.btn-secondary {
  background-color: transparent;
  margin: 0;
  color: white;
  width: fit-content;
  border: none;
  font-size: 12px !important;
  cursor: pointer;
  font-weight: bold;
  padding: 9px 20px !important;
}

.btn--primary:hover {
  background-color: #003b77;
}
</style>
